import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Van } from "@phosphor-icons/react";

const EMPTY = { name: "", phone: "" };

export default function Transporters() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const { nameFor } = useUserNames();

  const load = async () => {
    const { data } = await client.get("/transporters");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ name: r.name, phone: r.phone || "" }); setEditId(r.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Transporter name is required");
    try {
      if (editId) await client.put(`/transporters/${editId}`, form);
      else await client.post("/transporters", form);
      toast.success(editId ? "Updated" : "Transporter added");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this transporter?")) return;
    try { await client.delete(`/transporters/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = rows.filter(r => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (r.name || "").toLowerCase().includes(s) || (r.phone || "").includes(s);
  });

  return (
    <div>
      <PageHeader
        title="Transporter Master"
        subtitle="Manage transporters used for dispatch"
        actions={
          <Button data-testid="transporter-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
            <Plus size={16} weight="bold" className="mr-1"/> New Transporter
          </Button>
        }
      />
      <Section>
        <div className="mb-4 max-w-sm">
          <Input
            data-testid="transporter-search-input"
            placeholder="Search by name or phone"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            className="rounded-sm"
          />
        </div>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3">Transporter Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Entered By</th>
                <th className="px-4 py-3">Created At</th>
                {isAdmin && <th className="px-4 py-3 w-24">Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="transporters-tbody">
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-semibold flex items-center gap-2">
                    <Van size={16} className="text-slate-500" weight="duotone"/> {r.name}
                  </td>
                  <td className="px-4 py-2 font-mono">{r.phone || "—"}</td>
                  <td className="px-4 py-2 text-xs">{nameFor(r.entered_by)}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button
                          data-testid={`transporter-edit-${r.id}`}
                          onClick={() => openEdit(r)}
                          className="p-1.5 hover:bg-slate-100 rounded-sm"
                        ><PencilSimple size={14}/></button>
                        <button
                          data-testid={`transporter-delete-${r.id}`}
                          onClick={() => remove(r.id)}
                          className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"
                        ><Trash size={14}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 5 : 4} className="px-4 py-10 text-center text-slate-500">
                  {rows.length === 0 ? "No transporters yet — add your first one" : "No transporters match your search"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit" : "New"} Transporter</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <label className="label-cap">Transporter Name *</label>
              <Input
                data-testid="transporter-name-input"
                value={form.name}
                onChange={(e)=>setForm({ ...form, name: e.target.value })}
                autoFocus
              />
            </div>
            <div>
              <label className="label-cap">Transporter Phone</label>
              <Input
                data-testid="transporter-phone-input"
                value={form.phone}
                onChange={(e)=>setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="transporter-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
